import { X, Filter, Trash2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { Badge } from './badge'

interface FilterOption {
  id: string
  label: string
  count: number
  active: boolean
}

interface FilterDrawerProps {
  isOpen: boolean
  onClose: () => void
  filters: FilterOption[]
  onToggleFilter: (filterId: string) => void
  onClearAll: () => void
  activeFiltersCount: number
}

export function FilterDrawer({
  isOpen,
  onClose,
  filters,
  onToggleFilter,
  onClearAll,
  activeFiltersCount
}: FilterDrawerProps) {
  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-40 transition-opacity md:hidden",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={cn(
          "fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 transition-transform duration-300 ease-out md:hidden",
          "max-h-[85vh] flex flex-col",
          isOpen ? "translate-y-0" : "translate-y-full"
        )}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">
              Filters
              {activeFiltersCount > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({activeFiltersCount} active)
                </span>
              )}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close filters"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 overscroll-contain">
          <div className="space-y-3">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => onToggleFilter(filter.id)}
                className={cn(
                  "w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all min-h-[56px]",
                  filter.active
                    ? "bg-blue-50 border-blue-500"
                    : "bg-white border-gray-200 hover:border-gray-300 active:bg-gray-50"
                )}
              >
                <div className="flex items-center space-x-3 flex-1">
                  <div
                    className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                      filter.active
                        ? "bg-blue-500 border-blue-500"
                        : "bg-white border-gray-300"
                    )}
                  >
                    {filter.active && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <span
                    className={cn(
                      "text-base font-medium",
                      filter.active ? "text-blue-900" : "text-gray-700"
                    )}
                  >
                    {filter.label}
                  </span>
                </div>
                <Badge
                  variant={filter.active ? "default" : "secondary"}
                  className="ml-2"
                >
                  {filter.count}
                </Badge>
              </button>
            ))}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 safe-area-bottom">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onClearAll}
              disabled={activeFiltersCount === 0}
              className="flex-1 min-h-[48px] text-base"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All
            </Button>
            <Button
              onClick={onClose}
              className="flex-1 min-h-[48px] text-base bg-[#42a5f5] hover:bg-[#1976d2]"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
